import type { Product, ProductXeroPush } from '@/lib/types';
import type { XeroRowPushReport } from '@/lib/xero';

/** The push status off a product save, whichever wire shape it arrived in. */
function pushStatusOf(push: ProductXeroPush | undefined): 'pushed' | 'skipped' | 'failed' | null {
  if (push === undefined) return null;
  if (typeof push === 'string') return push;
  return push.status;
}

/**
 * The banner a product save leaves behind, in `XeroPushReportBanner`'s shape —
 * or `null` for "say nothing", which is the ordinary answer: `pushed` and
 * `skipped` are normal, and an older API sends no `xeroPush` at all.
 *
 * Only a FAILED push is reported, and the wording leads with "saved": the
 * product was stored whatever Xero said, and a heading that reads as a failed
 * save invites somebody to create it again.
 */
export function productPushReport(
  saved: Product,
  push: ProductXeroPush | undefined,
): XeroRowPushReport | null {
  if (pushStatusOf(push) !== 'failed') return null;
  const lines: string[] = [];
  if (push !== undefined && typeof push !== 'string' && push.status === 'failed') {
    if (push.message.trim() !== '') lines.push(push.message);
  } else {
    const error = saved.xeroSync?.error?.trim() ?? '';
    if (error !== '') lines.push(error);
  }
  return {
    tone: 'danger',
    heading: `${saved.productName} saved. Xero did not accept the item.`,
    lines,
    pointer: null,
  };
}
